import axios from 'axios';
import { logApiCallFailed, logSlowApiResponse } from '../utils/logger';

// Base API configuration.
// Dev: leave REACT_APP_API_BASE_URL unset and rely on the CRA proxy ('/api/v1').
// Prod (static hosting like Cloudflare Pages, different origin from the API): set
// REACT_APP_API_BASE_URL or REACT_APP_API_URL to the backend URL, e.g.
// https://tutamen-production.up.railway.app/api/v1
const DEV_PROXY_API_URL = '/api/v1';
const apiBaseURL = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'development' ? DEV_PROXY_API_URL : null);
if (process.env.NODE_ENV === 'production' && !apiBaseURL) {
  throw new Error('[tutamen] Missing production API base URL. Set REACT_APP_API_BASE_URL or REACT_APP_API_URL in Cloudflare Pages environment variables to the backend URL, e.g. https://your-backend.example.com/api/v1');
}

const api = axios.create({
  baseURL: apiBaseURL,
  headers: {
    'Content-Type': 'application/json'
  }
});

const SLOW_RESPONSE_MS = 2000;

// Add auth token and request start time (for logging)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.metadata = { startTime: Date.now(), url: config.url };
  return config;
});

// Logging + redirect on 401
api.interceptors.response.use(
  (response) => {
    const start = response.config?.metadata?.startTime;
    if (typeof start === 'number') {
      const duration = Date.now() - start;
      if (duration > SLOW_RESPONSE_MS) {
        const endpoint = response.config?.url || response.config?.metadata?.url || '';
        logSlowApiResponse(endpoint, duration, SLOW_RESPONSE_MS);
      }
    }
    return response;
  },
  (error) => {
    const endpoint = error.config?.url || error.config?.metadata?.url || error.config?.baseURL || '';
    const status = error.response?.status;
    const message = error.response?.data?.message || error.response?.data?.error?.message || error.message || 'Unknown error';
    logApiCallFailed(endpoint, status || 0, message, 0);
    if (error.response?.status === 401) {
      const hadAuthToken = !!localStorage.getItem('auth_token');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      if (hadAuthToken && window.location.pathname !== '/login') {
        window.location.replace('/login');
      }
    }
    return Promise.reject(error);
  }
);

// KYC API methods
export const kycAPI = {
  // Start KYC session
  startSession: async (insuranceProductType = 'health', applicantType = 'individual') => {
    const response = await api.post('/kyc/start', {
      insurance_product_type: insuranceProductType,
      applicant_type: applicantType
    });
    return response.data;
  },

  // Get session state
  getSession: async (sessionId) => {
    const response = await api.get(`/kyc/session/${sessionId}`);
    return response.data;
  },

  // Upload document
  uploadDocument: async (sessionId, stepCode, documentType, imageData) => {
    const response = await api.post('/kyc/document/upload', {
      session_id: sessionId,
      step_code: stepCode,
      document_type: documentType,
      image_data: imageData,
      metadata: {
        capture_method: 'camera',
        document_side: stepCode.includes('back') ? 'back' : 'front'
      }
    });
    return response.data;
  },

  // Submit corrections
  submitCorrections: async (documentId, corrections) => {
    const response = await api.post(`/kyc/document/${documentId}/correct`, {
      corrections
    });
    return response.data;
  }
};

// Auth API methods (for later)
// Wallet / Policy API
export const walletAPI = {
  getStats: () => api.get('/wallet/stats').then(r => r.data?.data ?? r.data),
  listPolicies: (params) => api.get('/policies', { params }).then(r => r.data?.data ?? r.data),
  getPolicy: (id) => api.get(`/policies/${id}`).then(r => r.data?.data ?? r.data),
  createPolicy: (data) => api.post('/policies', data).then(r => r.data),
  updatePolicy: (id, data) => api.put(`/policies/${id}`, data).then(r => r.data),
  deletePolicy: (id) => api.delete(`/policies/${id}`).then(r => r.data),
  lookupPolicy: (insurerId, policyNumber) => api.post('/policies/lookup', { insurer_id: insurerId, policy_number: policyNumber }).then(r => r.data),
  uploadDocument: (formData) => api.post('/policies/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data),
  getLimits: (policyId) => api.get(`/policies/${policyId}/limits`).then(r => r.data?.data ?? r.data),
  getDocuments: (policyId) => api.get(`/policies/${policyId}/documents`).then(r => r.data?.data ?? r.data),
  addDocument: (policyId, doc) => api.post(`/policies/${policyId}/documents`, doc).then(r => r.data?.data ?? r.data),
  deleteDocument: (policyId, docId) => api.delete(`/policies/${policyId}/documents/${docId}`).then(r => r.data),
  getMembers: (policyId) => api.get(`/policies/${policyId}/members`).then(r => r.data?.data ?? r.data),
  addMember: (policyId, member) => api.post(`/policies/${policyId}/members`, member).then(r => r.data),
  updateMember: (policyId, memberId, data) => api.put(`/policies/${policyId}/members/${memberId}`, data).then(r => r.data),
  removeMember: (policyId, memberId) => api.delete(`/policies/${policyId}/members/${memberId}`).then(r => r.data),
  getInsurers: (subcategory) => api.get('/policies/insurers/list', subcategory ? { params: { subcategory } } : {}).then(r => r.data?.data ?? r.data),
  uploadMemberPhoto: (formData) => api.post('/upload/member-photo', formData, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data?.url ?? r.data),
  uploadClaimDocument: (formData) => api.post('/upload/claim-document', formData, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data)
};

// Claims API (Phase 10C)
export const claimsAPI = {
  list: (params) => api.get('/claims', { params }).then(r => r.data?.data ?? r.data),
  get: (id) => api.get(`/claims/${id}`).then(r => r.data?.data ?? r.data),
  create: (data) => api.post('/claims', data).then(r => r.data?.data ?? r.data),
  update: (id, data) => api.put(`/claims/${id}`, data).then(r => r.data?.data ?? r.data),
  delete: (id) => api.delete(`/claims/${id}`).then(r => r.data),
  submit: (id, body) => api.post(`/claims/${id}/submit`, body || {}).then(r => r.data?.data ?? r.data),
  cancel: (id, body) => api.post(`/claims/${id}/cancel`, body || {}).then(r => r.data?.data ?? r.data),
  getDocuments: (id) => api.get(`/claims/${id}/documents`).then(r => r.data?.data ?? r.data),
  addDocument: (id, doc) => api.post(`/claims/${id}/documents`, doc).then(r => r.data?.data ?? r.data),
  deleteDocument: (id, docId) => api.delete(`/claims/${id}/documents/${docId}`).then(r => r.data),
  getMessages: (id) => api.get(`/claims/${id}/messages`).then(r => r.data?.data ?? r.data),
  addMessage: (id, body) => api.post(`/claims/${id}/messages`, body).then(r => r.data?.data ?? r.data),
  markMessagesRead: (id) => api.put(`/claims/${id}/messages/read`).then(r => r.data),
  getStatusHistory: (id) => api.get(`/claims/${id}/status-history`).then(r => r.data?.data ?? r.data),
  validate: (data) => api.post('/claims/validate', data).then(r => r.data?.data ?? r.data),
  estimate: (data) => api.post('/claims/estimate', data).then(r => r.data?.data ?? r.data)
};

// Policy claims (list by policy)
export const policyClaimsAPI = {
  list: (policyId) => api.get(`/policies/${policyId}/claims`).then(r => r.data?.data ?? r.data)
};

// Renewals API (Phase 10D) - see docs/RENEWALS_API_CONTRACT.md
function normalizeUpcomingItem(item) {
  if (!item) return item;
  return {
    ...item,
    id: item.id ?? item.policyId,
    policy_name: item.policy_name ?? item.policyName,
    policy_name_ar: item.policy_name_ar ?? item.policyNameAr,
    policy_type: item.policy_type ?? item.policyType,
    insurer_name: item.insurer_name ?? item.insurerName,
    insurer_logo: item.insurer_logo ?? item.insurerLogo,
    premium_amount: item.premium_amount ?? item.currentPremium,
    end_date: item.end_date ?? item.expiryDate,
    better_deals_found: item.better_deals_found ?? item.betterDealsFound ?? 0,
    potential_savings: item.potential_savings ?? item.potentialSavings,
    last_check_date: item.last_check_date ?? item.lastCheckDate,
  };
}

export const renewalsAPI = {
  getUpcoming: (params = {}) => {
    const { limit = 20, filter, offset = 0, sort = 'expiry_date', order = 'asc' } = typeof params === 'number' ? { limit: params } : params;
    const query = { limit, offset, sort, order };
    if (filter) query.filter = filter;
    return api.get('/renewals/upcoming', { params: query })
      .then(r => (r.data?.data ?? r.data) || [])
      .then(arr => Array.isArray(arr) ? arr.map(normalizeUpcomingItem) : []);
  },
  getStats: () => api.get('/renewals/stats').then(r => r.data?.data ?? r.data),
  runCheck: (policyId, checkType) => api.post(`/renewals/policies/${policyId}/check`, { check_type: checkType || 'ad_hoc' }).then(r => r.data?.data ?? r.data),
  checkNow: (policyId) => api.get(`/renewals/policies/${policyId}/check-now`).then(r => r.data?.data ?? r.data),
  getRecommendations: (policyId) => api.get(`/renewals/policies/${policyId}/recommendations`).then(r => r.data?.data ?? r.data),
  getBestRecommendation: (policyId) => api.get(`/renewals/policies/${policyId}/recommendations/best`).then(r => r.data?.data ?? r.data),
  dismissRecommendation: (recId, reason) => api.post(`/renewals/recommendations/${recId}/dismiss`, { reason }).then(r => r.data),
  getSettings: (policyId) => api.get(`/renewals/policies/${policyId}/settings`).then(r => r.data?.data ?? r.data),
  updateSettings: (policyId, body) => api.put(`/renewals/policies/${policyId}/settings`, body).then(r => r.data?.data ?? r.data),
  getPremiumHistory: (policyId) => api.get(`/renewals/policies/${policyId}/premium-history`).then(r => r.data?.data ?? r.data),
  recordPremiumChange: (policyId, body) => api.post(`/renewals/policies/${policyId}/premium-history`, body).then(r => r.data?.data ?? r.data),
  renewWithCurrent: (policyId) => api.post(`/renewals/policies/${policyId}/renew`).then(r => r.data?.data ?? r.data),
  switchToNew: (policyId, recommendationId) => api.post(`/renewals/policies/${policyId}/switch`, { recommendation_id: recommendationId }).then(r => r.data?.data ?? r.data),
  shareRecommendation: (recommendationId, body) =>
    api.post(`/renewals/recommendations/${recommendationId}/share`, {
      method: body?.method || 'email',
      recipient_email: body?.recipientEmail ?? body?.recipient_email,
      personal_message: body?.personalMessage ?? body?.personal_message,
    }).then(r => r.data?.data ?? r.data),
};

// Digital cards API
export const cardsAPI = {
  listCards: () => api.get('/cards').then(r => r.data?.data ?? r.data),
  getCard: (id) => api.get(`/cards/${id}`).then(r => r.data?.data ?? r.data),
  getQR: (id) => api.get(`/cards/${id}/qr`).then(r => r.data?.data ?? r.data),
  downloadCard: (id) => api.get(`/cards/${id}/download`).then(r => r.data?.data ?? r.data),
  validateScan: (cardId, sessionToken) => api.post(`/cards/${cardId}/validate`, { session_token: sessionToken }).then(r => r.data?.data ?? r.data)
};

export const authAPI = {
  // turnstileToken comes from the Cloudflare Turnstile widget (production). In local dev without a
  // configured site key, callers pass 'dev-bypass', which the backend only accepts when
  // NODE_ENV !== 'production'.
  login: async (email, password, turnstileToken = 'dev-bypass') => {
    const response = await api.post('/auth/login', {
      email,
      password,
      turnstile_token: turnstileToken
    });
    return response.data;
  },

  verifyMFA: async (preMfaToken, totpCode) => {
    const response = await api.post('/mfa/verify', {
      method_type: 'totp',
      code: totpCode
    }, {
      headers: {
        Authorization: `Bearer ${preMfaToken}`
      }
    });
    return response.data;
  }
};

export default api;
